<template>
  <v-dialog v-model="dialog" persistent scrollable max-width="860px">
    <v-card v-draggable-dialog class="profile-dialog-card curriculum-subject-dialog">
      <div class="dialog-drag-handle profile-dialog-header curriculum-subject-dialog__header">
        <div class="curriculum-subject-dialog__heading">
          <div class="curriculum-subject-dialog__icon"><v-icon color="primary">mdi-book-plus-outline</v-icon></div>
          <div>
            <span>Curriculum subjects</span>
            <h2>Assign subjects</h2>
            <p v-if="selectedCurriculum" class="text-capitalize">{{ selectedCurriculum.name || "Unnamed curriculum" }}</p>
          </div>
        </div>
        <div class="curriculum-subject-dialog__header-actions">
          <div class="curriculum-total" :class="totalStatusClass">
            <div><strong>{{ formattedTotalMarks }}%</strong><span>of 100%</span></div>
            <small>{{ totalStatusText }}</small>
          </div>
          <v-btn icon aria-label="Close subject assignment" @click="close"><v-icon>mdi-close</v-icon></v-btn>
        </div>
      </div>

      <div v-if="selectedCurriculum" class="curriculum-subject-dialog__composer">
        <v-autocomplete
          v-model="selectedSubject"
          class="curriculum-subject-dialog__subject"
          dense
          autofocus
          hide-details
          :items="availableSubjects"
          clearable
          item-value="id"
          item-text="name"
          return-object
          label="Select subject"
          outlined
        >
          <template slot="item" slot-scope="{ item }">
            <v-list-item-content><v-list-item-title class="text--primary text-capitalize" v-text="item.name" /></v-list-item-content>
          </template>
        </v-autocomplete>
        <v-text-field
          v-model="marks"
          class="curriculum-subject-dialog__marks"
          outlined
          type="number"
          inputmode="decimal"
          dense
          hide-details
          label="Marks"
          min="0.01"
          max="100"
          step="0.01"
          suffix="%"
          @keyup.enter="add"
        />
        <v-btn color="primary" depressed :disabled="!canAddSubject" @click="add"><v-icon left small>mdi-plus</v-icon>Add subject</v-btn>
      </div>

      <v-card-text class="profile-dialog-body curriculum-subject-dialog__body">
        <draggable v-model="curriculumSubjectList" handle=".drag-handle" animation="150">
          <div v-for="(row, index) in curriculumSubjectList" :key="row.id + '-' + index" class="subject-row">
            <div class="subject-row__order drag-handle" :aria-label="`Reorder ${row.name}`"><v-icon color="grey darken-1">mdi-drag</v-icon><span>{{ index + 1 }}</span></div>
            <div class="subject-row__name text-capitalize">{{ row.name }}</div>
            <v-text-field
              v-model="row.marks"
              class="subject-row__marks"
              hide-details
              outlined
              type="number"
              inputmode="decimal"
              dense
              label="Marks"
              min="0.01"
              max="100"
              step="0.01"
              suffix="%"
              :error="!isValidMark(row.marks)"
            />
            <v-btn icon aria-label="Remove subject" @click="deleteRow(index)"><v-icon color="red">mdi-delete-outline</v-icon></v-btn>
          </div>
        </draggable>

        <div v-if="!curriculumSubjectList.length" class="curriculum-subject-dialog__empty">
          <v-icon size="38" color="grey lighten-1">mdi-book-plus-outline</v-icon>
          <strong>No subjects added yet</strong>
          <span>Select a subject above and assign its percentage.</span>
        </div>
      </v-card-text>

      <v-card-actions class="profile-dialog-actions curriculum-subject-dialog__actions">
        <div class="curriculum-subject-dialog__summary">
          <strong>{{ curriculumSubjectList.length }}</strong>
          <span>Subject{{ curriculumSubjectList.length === 1 ? "" : "s" }}</span>
          <v-divider vertical class="mx-3" />
          <strong :class="isTotalValid ? 'success--text' : 'warning--text text--darken-3'">{{ formattedTotalMarks }}%</strong>
          <span>Total</span>
        </div>
        <v-spacer />
        <v-btn text :disabled="loading" @click="close">Cancel</v-btn>
        <v-btn color="primary" depressed :loading="loading" :disabled="!canSave" @click="submit"><v-icon left small>mdi-content-save-outline</v-icon>Save subjects</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import curriculumService from "@/modules/ReportCard/Curriculums/service.js";
import subjectService from "@/modules/ReportCard/Subject/service.js";
import draggable from "vuedraggable";

export default {
  name: "Assign-Class-to-student",
  props: {
    editDialog: {
      default: false,
    },
    selectedCurriculum: {
      type: Object,
      default: null,
    },
  },
  components: {
    draggable,
  },
  data() {
    return {
      Subjects: [],
      selectedSubject: null,
      marks: null,
      curriculumSubjectList: [],
      loading: false,
      dialog: false,
    };
  },
  computed: {
    totalMarks() {
      const total = this.curriculumSubjectList.reduce((sum, row) => {
        const marks = Number(row.marks);
        return sum + (Number.isFinite(marks) ? marks : 0);
      }, 0);
      return Number(total.toFixed(4));
    },

    formattedTotalMarks() {
      return Number(this.totalMarks.toFixed(2)).toString();
    },

    isTotalValid() {
      return Math.abs(this.totalMarks - 100) < 0.0001;
    },

    canAddSubject() {
      return Boolean(this.selectedSubject) && this.isValidMark(this.marks);
    },

    canSave() {
      return this.curriculumSubjectList.length > 0 &&
        this.curriculumSubjectList.every((row) => this.isValidMark(row.marks)) &&
        this.isTotalValid;
    },

    totalStatusClass() {
      if (this.isTotalValid) return "curriculum-total--complete";
      if (this.totalMarks > 100) return "curriculum-total--over";
      return "curriculum-total--incomplete";
    },

    totalStatusText() {
      if (this.isTotalValid) return "Ready to save";
      if (this.totalMarks > 100) return `${Number((this.totalMarks - 100).toFixed(2))}% over`;
      return `${Number((100 - this.totalMarks).toFixed(2))}% remaining`;
    },

    availableSubjects() {
      const assignedIds = this.curriculumSubjectList.map((row) => row.id);
      return this.Subjects.filter((subject) => !assignedIds.includes(subject.id));
    },
  },
  watch: {
    editDialog(val) {
      this.dialog = val;
    },

    dialog(val) {
      if (val) {
        this.getSubject();

        if (
          this.selectedCurriculum &&
          this.selectedCurriculum.subjectMeta &&
          this.selectedCurriculum.subjectMeta.subjects
        ) {
          this.curriculumSubjectList = JSON.parse(
            JSON.stringify(this.selectedCurriculum.subjectMeta.subjects)
          );
        } else {
          this.curriculumSubjectList = [];
        }

        console.log(
          "selectedCurriculum in assign subject",
          this.selectedCurriculum
        );
      }
    },
  },
  methods: {
    isValidMark(value) {
      const marks = Number(value);
      return Number.isFinite(marks) && marks > 0 && marks <= 100;
    },

    deleteRow(i) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#B71C1C",
        cancelButtonColor: "#43A047",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          this.curriculumSubjectList.splice(i, 1);
        }
      });
    },

    add() {
      if (!this.canAddSubject) return;

      const obj = {
        id: this.selectedSubject.id,
        marks: Number(this.marks),
        name: this.selectedSubject.name,
      };

      this.curriculumSubjectList.push(obj);
      this.selectedSubject = null;
      this.marks = null;
    },

    getSubject() {
      return subjectService.getAll().then((response) => {
        this.Subjects = response.data || [];
        console.log("response Subjects", this.Subjects);
        return response;
      });
    },

    submit() {
      if (!this.canSave) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: "Total marks should be equal to 100",
          });
        return true;
      }

      this.loading = true;

      this.curriculumSubjectList = this.curriculumSubjectList.map((row, idx) => ({
        ...row,
        order: idx + 1,
        marks: Number(row.marks),
      }));

      const payload = {
        ...this.selectedCurriculum,
        subjectMeta: {
          subjects: this.curriculumSubjectList,
        },
      };

      curriculumService
        .update(this.selectedCurriculum.id, payload)
        .then((result) => {
          if (result) {
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "success",
                title: "Subjects assigned to this curriculum",
              });

            this.loading = false;
            this.empty();
            this.$emit("submit");
          }
        })
        .catch((err) => {
          this.loading = false;
          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "error",
              title: err.data.message,
            });
        });
    },

    close() {
      this.$emit("close");
      this.empty();
    },

    empty() {
      this.selectedSubject = null;
      this.marks = null;
      this.curriculumSubjectList = [];
    },
  },
};
</script>

<style scoped>
.curriculum-subject-dialog { overflow: hidden; }
.curriculum-subject-dialog__header { display: flex; min-height: 92px; align-items: center; justify-content: space-between; gap: 20px; cursor: move; }
.curriculum-subject-dialog__heading { display: flex; min-width: 0; align-items: center; gap: 12px; }
.curriculum-subject-dialog__icon { display: flex; width: 42px; height: 42px; flex: 0 0 42px; align-items: center; justify-content: center; border-radius: 10px; background: #eaf6eb; }
.curriculum-subject-dialog__heading span { color: #43a047; font-size: 10px; font-weight: 750; letter-spacing: .08em; text-transform: uppercase; }
.curriculum-subject-dialog__heading h2 { margin: 1px 0 0; color: #172033; font-size: 21px; line-height: 1.2; }
.curriculum-subject-dialog__heading p { overflow: hidden; margin: 2px 0 0; color: #7b8799; font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.curriculum-subject-dialog__header-actions { display: flex; align-items: center; gap: 10px; }
.curriculum-total { min-width: 156px; padding: 9px 12px; border: 1px solid; border-radius: 10px; cursor: default; }
.curriculum-total > div { display: flex; align-items: baseline; gap: 5px; }
.curriculum-total strong { font-size: 20px; line-height: 1; }
.curriculum-total span, .curriculum-total small { font-size: 10px; }
.curriculum-total small { display: block; margin-top: 4px; font-weight: 600; }
.curriculum-total--complete { border-color: #a5d6a7; background: #eff8f0; color: #2e7d32; }
.curriculum-total--incomplete { border-color: #ffe082; background: #fff9e8; color: #8d6e00; }
.curriculum-total--over { border-color: #ef9a9a; background: #fff2f2; color: #c62828; }
.curriculum-subject-dialog__composer { display: flex; align-items: center; gap: 12px; padding: 16px 24px; border-bottom: 1px solid #e7ece7; background: #fbfcfb; }
.curriculum-subject-dialog__subject { flex: 1 1 auto; }
.curriculum-subject-dialog__marks { max-width: 150px; }
.curriculum-subject-dialog__body { min-height: 300px; padding: 18px 24px !important; background: #f7f9f7; }
.subject-row { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; padding: 11px 12px; border: 1px solid #e1e7e1; border-radius: 11px; background: #fff; }
.subject-row__order { display: flex; width: 52px; flex: 0 0 52px; align-items: center; gap: 4px; color: #7b8799; cursor: grab; }
.subject-row__order:active { cursor: grabbing; }
.subject-row__name { min-width: 0; flex: 1 1 auto; color: #253047; font-size: 13px; font-weight: 600; }
.subject-row__marks { max-width: 140px; }
.curriculum-subject-dialog__empty { display: flex; min-height: 230px; align-items: center; justify-content: center; flex-direction: column; color: #899489; text-align: center; }
.curriculum-subject-dialog__empty strong { margin-top: 10px; color: #465568; }
.curriculum-subject-dialog__empty span { margin-top: 3px; font-size: 12px; }
.curriculum-subject-dialog__actions { min-height: 70px; }
.curriculum-subject-dialog__summary { display: flex; align-items: center; color: #7b8799; font-size: 11px; }
.curriculum-subject-dialog__summary strong { margin-right: 4px; color: #253047; font-size: 14px; }
.curriculum-subject-dialog >>> .v-input__slot { border-radius: 8px; }
@media (max-width: 600px) {
  .curriculum-subject-dialog__header { min-height: 82px; padding: 14px 16px; }
  .curriculum-subject-dialog__icon, .curriculum-total span { display: none; }
  .curriculum-total { min-width: 108px; }
  .curriculum-subject-dialog__composer { flex-wrap: wrap; padding: 12px 16px; }
  .curriculum-subject-dialog__subject { flex-basis: 100%; }
  .curriculum-subject-dialog__marks { max-width: none; flex: 1 1 auto; }
  .curriculum-subject-dialog__body { padding: 14px 12px !important; }
  .subject-row { gap: 7px; padding: 9px 7px; }
  .subject-row__order { width: 34px; flex-basis: 34px; }
  .subject-row__marks { max-width: 105px; }
  .curriculum-subject-dialog__summary { display: none; }
}
</style>
